import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicNavbar } from '../components/layout/PublicNavbar';
import { AuroraBG } from '../components/ui/AuroraBG';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Button } from '../components/ui/Button';
import { PricingTierCard } from '../components/pricing/PricingTierCard';
import { AddOnModuleCard } from '../components/pricing/AddOnModuleCard';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Zap,
  Crown,
  Rocket,
  Landmark,
  CreditCard,
  Wallet,
  Brain,
  ScanLine,
  CalendarClock,
  Activity,
  Award,
  ClipboardCheck,
  Bot,
  AlertCircle,
  Calculator,
  ArrowRight,
} from 'lucide-react';

const tiers = [
  {
    id: 'starter',
    name: 'STARTER',
    slug: 'starter',
    description: 'Perfect for single-location operations getting started with smart F&B management',
    priceMonthly: 149,
    priceAnnual: 1490,
    icon: Zap,
    features: [
      'Basic inventory management',
      'Recipe costing & menu engineering',
      'Sales data import from POS',
      'Basic MyDATA integration',
      '1 mobile app (choose one)',
      '3 user accounts',
      'Email support (48h response)',
      'Basic reporting & exports',
      'Standard onboarding',
    ],
  },
  {
    id: 'operations-pro',
    name: 'OPERATIONS PRO',
    slug: 'operations-pro',
    description: 'Complete AI-powered operations for restaurants and small chains',
    priceMonthly: 249,
    priceAnnual: 2490,
    icon: Crown,
    isPopular: true,
    features: [
      'Everything in STARTER',
      'All 15 AI modules included',
      'OCR Lite (100 docs/month)',
      'All 9 mobile PWA apps',
      'Full MyDATA sync & VAT reconciliation',
      'Core financial reconciliation',
      'Unlimited user accounts',
      'Unlimited locations',
      'Priority email support (24h response)',
      'Advanced analytics & dashboards',
      'Warehouse management pro',
      'Automated receiving & verification',
      'Variance detection & alerts',
      'Standard API access',
    ],
  },
  {
    id: 'enterprise',
    name: 'ENTERPRISE',
    slug: 'enterprise',
    description: 'Full-scale AI ERP + Fintech OS for multi-location chains and enterprise operations',
    priceMonthly: 555,
    priceAnnual: 5550,
    icon: Rocket,
    features: [
      'Everything in OPERATIONS PRO',
      'Full automation engine (unlimited workflows)',
      'Advanced AI engines (all features unlocked)',
      'B2B marketplace integration',
      'Treasury dashboard & cash flow forecasting',
      'Banking integration (all banks)',
      'POS terminal integration (all systems)',
      'AI financial intelligence',
      'Supplier intelligence & ranking',
      'AI shrinkage prevention',
      'AI scheduling & labor optimization',
      'Priority phone & chat support (4h response)',
      'Dedicated account manager',
      'White-label reporting',
      'Full API access with webhooks',
      'Custom integrations',
      'SLA guarantee (99.9% uptime)',
    ],
  },
];

const addOnModules = [
  {
    id: 'banking-module',
    name: 'Banking Module',
    description: 'Connect to all major Greek banks for automatic transaction import and reconciliation',
    price: 39,
    category: 'Fintech',
    icon: Landmark,
    features: [
      'Alpha Bank, Eurobank, Piraeus, Viva Bank',
      'Automatic transaction import',
      'Bank reconciliation',
      'Multi-account support',
      'Cash flow tracking',
    ],
  },
  {
    id: 'pos-terminal',
    name: 'POS Terminal Module',
    description: 'Full POS system with real-time inventory deduction and sales tracking',
    price: 29,
    category: 'Operations',
    icon: CreditCard,
    features: [
      'Touch-optimized interface',
      'Real-time stock updates',
      'Session management',
      'Receipt printing',
      'Multi-location support',
    ],
  },
  {
    id: 'pis-payments',
    name: 'PIS Payments',
    description: 'Payment Initiation Services for supplier payments and treasury management',
    price: 29,
    category: 'Fintech',
    icon: Wallet,
    features: [
      'Instant bank transfers',
      'Batch supplier payments',
      'Payment scheduling',
      'Multi-currency support',
      'Payment tracking & audit',
    ],
  },
  {
    id: 'ai-financial-intelligence',
    name: 'AI Financial Intelligence',
    description: 'Advanced financial analytics, forecasting, and anomaly detection',
    price: 49,
    category: 'AI Intelligence',
    icon: Brain,
    features: [
      'Predictive P&L forecasting',
      'Cash flow predictions',
      'Anomaly detection',
      'Financial risk scoring',
      'Smart recommendations',
    ],
  },
  {
    id: 'ocr-unlimited',
    name: 'OCR Unlimited',
    description: 'Unlimited invoice and document scanning with 98% AI accuracy',
    priceRange: { min: 29, max: 49 },
    category: 'AI Intelligence',
    icon: ScanLine,
    features: [
      'Unlimited document processing',
      'Multi-format support (PDF, images)',
      'Auto-categorization',
      'Data extraction & validation',
      'Integration with accounting',
    ],
  },
  {
    id: 'ai-scheduling',
    name: 'AI Scheduling',
    description: 'Intelligent staff scheduling based on demand forecasting and labor laws',
    priceRange: { min: 49, max: 69 },
    category: 'AI Intelligence',
    icon: CalendarClock,
    features: [
      'Demand-based scheduling',
      'Labor law compliance',
      'Shift swapping & approvals',
      'Overtime tracking',
      'Performance-based assignments',
    ],
  },
  {
    id: 'ai-shrinkage',
    name: 'AI Shrinkage Engine',
    description: 'Advanced loss prevention with alcohol tracking and predictive alerts',
    price: 49,
    category: 'AI Advanced',
    icon: Activity,
    features: [
      'Pour tracking for alcohol',
      'High-value item monitoring',
      'Predictive theft detection',
      'Behavioral pattern analysis',
      'Loss prevention alerts',
    ],
  },
  {
    id: 'supplier-intelligence',
    name: 'Supplier Intelligence',
    description: 'Automated supplier scoring, ranking, and performance tracking',
    price: 29,
    category: 'AI Intelligence',
    icon: Award,
    features: [
      'Automated supplier rating',
      'Price comparison & alerts',
      'Quality tracking',
      'Delivery performance',
      'Contract compliance',
    ],
  },
  {
    id: 'task-sop-pro',
    name: 'Task/SOP Pro',
    description: 'Advanced workflow management with AI quality assurance',
    price: 29,
    category: 'Operations',
    icon: ClipboardCheck,
    features: [
      'Custom workflow builder',
      'SOP compliance tracking',
      'AI quality checks',
      'Photo evidence capture',
      'Performance analytics',
    ],
  },
  {
    id: 'automations-pro',
    name: 'Automations Pro',
    description: 'Unlimited custom automation rules and workflows',
    price: 29,
    category: 'System',
    icon: Bot,
    features: [
      'Unlimited automation rules',
      'Custom trigger conditions',
      'Multi-step workflows',
      'Integration webhooks',
      'Advanced scheduling',
    ],
  },
];

export function PricingPage() {
  const { t } = useTranslation();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { currentTenant, user } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedAddOns, setSelectedAddOns] = useState<Set<string>>(new Set());
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [locations, setLocations] = useState(1);

  const handleUpgrade = async (tierId: string, tierSlug: string, tierName: string) => {
    if (!user) {
      alert('Please log in to subscribe to a plan.');
      return;
    }

    let tenantId = currentTenant;
    if (!tenantId && user) {
      const { data } = await supabase
        .from('user_tenants')
        .select('tenant_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (!data?.tenant_id) {
        alert('No company account found. Please complete your registration.');
        return;
      }
      tenantId = data.tenant_id;
    }

    if (!tenantId) {
      alert('Unable to load your account. Please refresh the page and try again.');
      return;
    }

    setUpgrading(tierId);

    try {
      const tier = tiers.find((t) => t.id === tierId);
      if (!tier) throw new Error('Tier not found');

      const basePrice = billingCycle === 'monthly' ? tier.priceMonthly : tier.priceAnnual;
      const totalPrice = basePrice * locations;

      const selectedAddOnsList = Array.from(selectedAddOns);
      let addOnsTotal = 0;
      selectedAddOnsList.forEach((addOnId) => {
        const addOn = addOnModules.find((a) => a.id === addOnId);
        if (addOn) {
          addOnsTotal += addOn.priceRange ? addOn.priceRange.min : addOn.price;
        }
      });
      addOnsTotal *= locations;

      const grandTotal = totalPrice + addOnsTotal;

      const confirmMessage = `
Subscription Details:
━━━━━━━━━━━━━━━━━━━━━

Plan: ${tierName}
Locations: ${locations}
Billing: ${billingCycle === 'monthly' ? 'Monthly' : 'Annual'}

Base Price: €${totalPrice}
Add-ons: €${addOnsTotal}
━━━━━━━━━━━━━━━━━━━━━
Total: €${grandTotal}${billingCycle === 'monthly' ? '/month' : '/year'}

━━━━━━━━━━━━━━━━━━━━━

This will redirect you to a secure payment page.

Payment Methods Accepted:
✓ Credit/Debit Cards (Visa, Mastercard, Amex)
✓ SEPA Direct Debit
✓ Bank Transfer

Note: This is a demo. In production, you would be redirected to Stripe Checkout.

Would you like to proceed?`;

      if (!confirm(confirmMessage)) {
        setUpgrading(null);
        return;
      }

      alert(
        '✓ Subscription activated successfully!\n\nIn production, payment would be processed via Stripe.\n\nYour new plan is now active.'
      );
      window.location.reload();
    } catch (error) {
      console.error('Subscription error:', error);
      alert(
        '⚠ Failed to process subscription.\n\nPlease try again or contact support at support@foodops.ai'
      );
    } finally {
      setUpgrading(null);
    }
  };

  const toggleAddOn = (addOnId: string) => {
    const newSet = new Set(selectedAddOns);
    if (newSet.has(addOnId)) {
      newSet.delete(addOnId);
    } else {
      newSet.add(addOnId);
    }
    setSelectedAddOns(newSet);
  };

  const calculateTotal = () => {
    let addOnsTotal = 0;
    Array.from(selectedAddOns).forEach((addOnId) => {
      const addOn = addOnModules.find((a) => a.id === addOnId);
      if (addOn) {
        addOnsTotal += addOn.priceRange ? addOn.priceRange.min : addOn.price;
      }
    });
    return addOnsTotal * locations;
  };

  if (subscriptionLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400" />
        </div>
      </DashboardLayout>
    );
  }

  const currentTierSlug = subscription?.tier?.slug;

  return (
    <>
      <AuroraBG />
      <PublicNavbar />
      <div className="min-h-screen relative pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Subscription & Pricing</h1>
          <p className="text-white/60">
            Choose the plan that fits your business needs. All plans include 14-day free trial.
          </p>
        </div>

        {subscription && (
          <GlassPanel className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Current Plan</h3>
                <p className="text-2xl font-bold text-primary-400">
                  {subscription.tier?.name || 'Unknown'}
                  <span className="text-sm text-white/60 ml-2 font-normal">
                    €{subscription.currentPrice}/
                    {subscription.billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </p>
              </div>
            </div>
          </GlassPanel>
        )}

        <GlassPanel className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="glass-panel p-1 inline-flex rounded-xl">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-primary-500 text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    billingCycle === 'annual'
                      ? 'bg-primary-500 text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Annual
                  <span className="ml-2 text-xs bg-success-500/20 text-success-400 px-2 py-0.5 rounded">
                    Save 17%
                  </span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-white/80">Locations:</label>
              <input
                type="number"
                min="1"
                max="100"
                value={locations}
                onChange={(e) => setLocations(Math.max(1, parseInt(e.target.value) || 1))}
                className="glass-input w-20 text-center"
              />
            </div>
          </div>
        </GlassPanel>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const isCurrentPlan = currentTierSlug === tier.slug;
            return (
              <PricingTierCard
                key={tier.id}
                name={tier.name}
                description={tier.description}
                priceMonthly={tier.priceMonthly * locations}
                priceAnnual={tier.priceAnnual ? tier.priceAnnual * locations : undefined}
                features={tier.features}
                icon={tier.icon}
                isPopular={tier.isPopular}
                isCurrent={isCurrentPlan}
                billingCycle={billingCycle}
                onSelect={() => handleUpgrade(tier.id, tier.slug, tier.name)}
                isLoading={upgrading === tier.id}
              />
            );
          })}
        </div>

        <div className="pt-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-3">Paid Add-On Modules</h2>
            <p className="text-white/60">
              Extend your plan with specialized modules tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addOnModules.map((addOn) => (
              <AddOnModuleCard
                key={addOn.id}
                name={addOn.name}
                description={addOn.description}
                price={addOn.price}
                priceRange={addOn.priceRange}
                features={addOn.features}
                icon={addOn.icon}
                category={addOn.category}
                isSelected={selectedAddOns.has(addOn.id)}
                onToggle={() => toggleAddOn(addOn.id)}
              />
            ))}
          </div>

          {selectedAddOns.size > 0 && (
            <GlassPanel className="p-6 mt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Selected Add-Ons</h3>
                  <p className="text-white/60 text-sm">
                    {selectedAddOns.size} module{selectedAddOns.size > 1 ? 's' : ''} selected
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/60 mb-1">Additional Monthly Cost</p>
                  <p className="text-3xl font-bold text-primary-400">+€{calculateTotal()}</p>
                </div>
              </div>
            </GlassPanel>
          )}
        </div>

        <GlassPanel className="p-8 text-center">
          <Calculator className="w-12 h-12 text-primary-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Need a Custom Solution?</h3>
          <p className="text-white/60 mb-6">
            Contact our sales team for custom pricing, dedicated infrastructure, enterprise SLAs, and
            tailored solutions for chains with 50+ locations.
          </p>
          <Button variant="primary">
            Contact Sales
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </GlassPanel>

        <div className="text-center">
          <p className="text-white/50 text-sm">
            All prices in EUR • 14-day free trial on all plans • No credit card required • Cancel
            anytime
          </p>
        </div>
        </div>
      </div>
    </>
  );
}
